import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { AccessControlModal } from "components/modals/access-control-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchApiKeys } from "data/queries-common";
import appsDark from "icons/apps-dark.png";
import appsLight from "icons/apps-light.png";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { ApiKeyTable } from "./api-keys-table";
import { CreateKeyDialog } from "./create-key-dialog";

export const premiumProducts = ["Buildings API", "Land Parcels API"];

export const ApiKeysPage = () => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { isPermitted } = usePermissions();
  const apiKeysQuery = useQuery({
      queryKey: [QueryKeys.apiKeys, identityId],
      queryFn: () =>
      fetchApiKeys(),
    });

  const keysPremiumData = apiKeysQuery.data?.some((key: any) =>
    key.products.some((product: any) => premiumProducts.includes(product))
  );

  const premiumDataPermission = [
    PermissionsKeys.premiumDataCreate,
    PermissionsKeys.premiumDataDelete,
    PermissionsKeys.premiumDataRead,
    PermissionsKeys.premiumDataUpdate,
  ];

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <>
      <PermissionsWrapper
        keys={[PermissionsKeys.appsCreate]}
        messages={{
          [PermissionsReasons.ROLE_DENY]: <></>,
          [PermissionsReasons.UPGRADE_REQUIRED]: <></>,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            position: "absolute",
            top: "20px",
            right: "40px",
          }}
        >
          <LoadingButton
            loading={apiKeysQuery.isPending}
            onClick={() => setCreateDialogOpen(true)}
            variant="contained"
          >
            Create API Key
          </LoadingButton>
        </Box>
        <CreateKeyDialog
          open={createDialogOpen}
          handleClose={() => {
            setCreateDialogOpen(false);
          }}
        />
      </PermissionsWrapper>

      {/* We got this double wrapper so we only show you the 'upgrade for premium data' CTA  */}
      <PermissionsWrapper
        keys={premiumDataPermission}
        messages={{
          [PermissionsReasons.ROLE_DENY]: <></>,
          [PermissionsReasons.UPGRADE_REQUIRED]: (
            <PermissionsWrapper
              keys={[PermissionsKeys.appsCreate]}
              messages={{
                [PermissionsReasons.UPGRADE_REQUIRED]: <></>,
              }}
              fallbackToChildren={[PermissionsReasons.ROLE_DENY]}
            >
              <Alert
                variant="outlined"
                severity="info"
                action={
                  <UpgradeSubscriptionButton
                    variant="text"
                    text="see subscriptions"
                  />
                }
              >
                <AlertTitle>
                  {keysPremiumData
                    ? "Premium APIs unavailable"
                    : "Upgrade for premium"}
                </AlertTitle>
                {keysPremiumData
                  ? "Looks like some of your Apps use premium data. To enable those APIs and data or create new Apps with access to premium data, please upgrade to a paid plan."
                  : "Want more from Apps? Upgrade to a paid plan for larger credit balances and access to premium APIs and data such as Land Parcels API, Buildings API and MailPoint."}
              </Alert>
            </PermissionsWrapper>
          ),
        }}
      >
        <></>
      </PermissionsWrapper>

      <Box
        style={{
          width: "100%",
          height: "100%",
          // overflow: "auto"
        }}
      >
        <ReactQueryErrorWrapper queries={[apiKeysQuery]} mutations={[]} />

        <br></br>
        <PermissionsWrapper
          keys={[PermissionsKeys.appsCreate]}
          messages={{
            [PermissionsReasons.UPGRADE_REQUIRED]: (
              <AccessControlModal
                title="Subscribe to access Apps"
                children={<AppInfo />}
                image={isDark ? appsDark : appsLight}
              />
            ),
          }}
          fallbackToChildren={[PermissionsReasons.ROLE_DENY]}
        >
          <ApiKeyTable
            data={apiKeysQuery.data}
            isLoading={apiKeysQuery.isPending}
            apiKeyDetailsRoute={Routes.developerApiKeysEditKey}
            handleClick={() => {}}
            identityId={identityId}
          />
        </PermissionsWrapper>
      </Box>
    </>
  );
};

const AppInfo = () => {
  return (
    <Box>
      <Typography variant="body2">
        Integrate address autocomplete, geocoding and maps into your apps and
        websites. Create and manage access through API Keys.
      </Typography>

      <Typography variant="body2">
        Premium can additionally return insights on:
      </Typography>

      <List sx={{ listStyleType: "disc", pl: 6, pt: 0, pb: 0 }}>
        <ListItem sx={{ display: "list-item", pt: 0, pb: 0 }}>
          <ListItemText
            primary="Buildings"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </ListItem>
        <ListItem sx={{ display: "list-item", pt: 0, pb: 0 }}>
          <ListItemText
            primary="Solar"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </ListItem>
        <ListItem sx={{ display: "list-item", pt: 0, pb: 0 }}>
          <ListItemText
            primary="Property"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </ListItem>
        <ListItem sx={{ display: "list-item", pt: 0, pb: 0 }}>
          <ListItemText
            primary="And more"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </ListItem>
      </List>

      <Typography variant="body2">
        Subscribe to get started with Apps
      </Typography>
    </Box>
  );
};

export default AppInfo;
